import type { TecnicaConsigliataType } from './types'

export const getPrezzoPersonalizzazione = (configurazione: any, quantita: number) => {
  if (quantita <= 0) return null

  const scaglione = configurazione?.prezzi_personalizzazione
    ?.filter((prezzo: any) => quantita >= prezzo.a_partire_da)
    .sort((a: any, b: any) => b.a_partire_da - a.a_partire_da)[0]

  return scaglione?.prezzo != null ? Number(scaglione.prezzo) : null
}

/**
 * Confronta stampa e impianti su tutte le posizioni oppure solo sull'indice richiesto.
 * La tecnica proposta deve comunque coprire tutte le posizioni, per poter applicare
 * la configurazione completa. Priorità crescente dall'API, poi ordine ricevuto.
 */
export const getTecnicaConsigliata = ({
  posizioni,
  indicePosizione = 0,
  confrontoSulTotale = true,
  tecnicaSelezionata,
  tecniche,
  prezzi,
  leadTime,
  quantita,
}: any): TecnicaConsigliataType | null => {
  if (!posizioni?.length) return null
  if (new Set(posizioni.map((posizione: any) => posizione.id)).size !== posizioni.length)
    return null

  // Lo stesso calcolo viene usato per la tecnica attuale e per ogni alternativa.
  const getTotale = (tecnica: any, posizione: any) => {
    const prezziTecnica = prezzi?.tecniche?.find((t: any) => t.id === tecnica?.id)
    const configurazione = posizione.colori?.personalizzazioni_objects?.[0]
    const configurazionePrezzi = prezziTecnica?.posizioni
      ?.find((p: any) => p.id === posizione.id)
      ?.numero_colori?.find((c: any) => c.id === posizione.colori?.id)
      ?.personalizzazioni_objects?.find((c: any) => c.id === configurazione?.id)
    const prezzo = getPrezzoPersonalizzazione(configurazionePrezzi, quantita)
    if (prezzo === null || prezziTecnica?.costo_impianto == null) return null
    // Il full color conta un solo impianto; i totali sono arrotondati in centesimi.
    const impianti = posizione.colori.full_color ? 1 : posizione.colori.numero_colori || 1
    return Math.round((prezzo * quantita + prezziTecnica.costo_impianto * impianti) * 100)
  }

  // La base del confronto è il totale delle posizioni oppure quello del singolo box.
  // Se manca un prezzo, il confronto non è attendibile e il consiglio resta nascosto.
  const totaliAttuali = posizioni.map((posizione: any) => getTotale(tecnicaSelezionata, posizione))
  if (totaliAttuali.some((totale: number | null) => totale === null)) return null
  const totaleAttuale = confrontoSulTotale
    ? totaliAttuali.reduce((somma: number, totale: number) => somma + totale, 0)
    : totaliAttuali[indicePosizione]
  if (!(totaleAttuale > 0)) return null

  // Valuta le alternative in ordine di priorità, senza modificare l'array ricevuto dall'API.
  const tecnicheConsigliate = (tecniche || [])
    .filter((tecnica: any) => tecnica.consigliata && tecnica.id !== tecnicaSelezionata?.id)
    .sort((a: any, b: any) => (a.ordinamento_consigliata ?? 0) - (b.ordinamento_consigliata ?? 0))

  for (const tecnica of tecnicheConsigliate) {
    const posizioniConsigliate = []
    const totaliConsigliati = []
    for (const posizione of posizioni) {
      const posizioneConsigliata = tecnica.posizioni?.find((p: any) => p.id === posizione.id)
      const coloriDisponibili = posizioneConsigliata?.numero_colori || []
      // Mantiene il numero di colori scelto, altrimenti usa la prima opzione disponibile.
      const stessiColori = coloriDisponibili.find((c: any) =>
        posizione.colori?.full_color
          ? c.full_color
          : !c.full_color && c.numero_colori === posizione.colori?.numero_colori
      )
      const colori = stessiColori || coloriDisponibili[0]
      const configurazioneConsigliata = colori?.personalizzazioni_objects?.[0]
      if (!configurazioneConsigliata) break

      // La combinazione posizione/colori deve avere almeno un fornitore disponibile.
      const disponibilita = leadTime?.tecniche
        ?.find((t: any) => t.id === tecnica.id)
        ?.posizioni?.find((p: any) => p.id === posizione.id)
        ?.numero_colori?.find((c: any) => c.id === colori.id)
        ?.personalizzazioni_objects?.find((c: any) => c.id === configurazioneConsigliata.id)
      if (!disponibilita?.fornitori?.length) break

      // Applica le dimensioni della configurazione scelta anche quando sono diverse.
      // I pantoni restano solo se il numero di colori è compatibile.
      const nuovaPosizione = {
        ...posizione,
        ...posizioneConsigliata,
        colori,
        selectedLarghezza: configurazioneConsigliata.dimensione_larghezza,
        selectedAltezza: configurazioneConsigliata.dimensione_altezza,
        pantoni_codici: stessiColori && !colori.full_color ? posizione.pantoni_codici || [] : [],
      }
      const totale = getTotale(tecnica, nuovaPosizione)
      if (totale === null) break
      posizioniConsigliate.push(nuovaPosizione)
      totaliConsigliati.push(totale)
    }
    // Anche nel confronto singolo, il cambio tecnica deve coprire tutte le posizioni.
    if (posizioniConsigliate.length !== posizioni.length) continue
    const totale = confrontoSulTotale
      ? totaliConsigliati.reduce((somma, prezzo) => somma + prezzo, 0)
      : totaliConsigliati[indicePosizione]
    // Restituisce la prima tecnica conveniente secondo la priorità API,
    // non necessariamente quella con il risparmio maggiore.
    if (totale < totaleAttuale) {
      return {
        tecnica,
        colori: posizioniConsigliate[indicePosizione]?.colori || posizioniConsigliate[0].colori,
        posizioni: posizioniConsigliate,
        totale: totale / 100,
        totaleAttuale: totaleAttuale / 100,
        risparmioPercentuale: ((totaleAttuale - totale) / totaleAttuale) * 100,
      }
    }
  }
  return null
}

export const getPosizioniSelezionateFromPersonalizzazione = (
  configurazioneScelta: any,
  configurazioniPossibili: any
) => {
  if (!configurazioneScelta?.posizioni || !configurazioniPossibili?.tecniche) {
    return []
  }

  const personalizzazioniMap = new Map()

  configurazioniPossibili.tecniche.forEach((tecnica: any) => {
    tecnica.posizioni.forEach((posizione: any) => {
      posizione.numero_colori.forEach((numeroColore: any) => {
        numeroColore.personalizzazioni_objects.forEach((po: any) => {
          personalizzazioniMap.set(po.id, {
            tecnica,
            posizione,
            numeroColore,
            matchPersonalizzazioneObject: po,
          })
        })
      })
    })
  })

  return configurazioneScelta.posizioni
    .map((initialPosizione: any) => {
      const selectedId = initialPosizione.personalizzazione_id
      const foundData = personalizzazioniMap.get(selectedId)

      if (!foundData) return null

      const { tecnica, posizione, numeroColore, matchPersonalizzazioneObject } = foundData

      return {
        ...posizione,
        colori: numeroColore,
        pantoni_codici: initialPosizione?.pantoni_codici || [],
        numero_colori: posizione.numero_colori,
        altezza: matchPersonalizzazioneObject.dimensione_altezza,
        selectedAltezza:
          initialPosizione?.altezza ?? matchPersonalizzazioneObject.dimensione_altezza,
        larghezza: matchPersonalizzazioneObject.dimensione_larghezza,
        selectedLarghezza:
          initialPosizione?.larghezza ?? matchPersonalizzazioneObject.dimensione_larghezza,
        posizione: posizione.nome,
        tecnica: tecnica.nome,
        tecnicaData: tecnica,
        descrizione_posizione: initialPosizione.descrizione_posizione || '',
        logo_file: initialPosizione.logo_file_url,
        logo_file_id: initialPosizione.logo_file,
        logo_filename: initialPosizione.logo_filename,
        tracciatura_logo: initialPosizione.tracciatura_logo,
      }
    })
    .filter(Boolean)
}

export const getConfigurazioneProdottoInitialValues = (
  configurazioneScelta: any,
  configurazioniPossibili: any
) => {
  if (!configurazioneScelta) return undefined

  const posizioni = getPosizioniSelezionateFromPersonalizzazione(
    configurazioneScelta,
    configurazioniPossibili
  )

  return {
    tecnica:
      posizioni?.[0]?.tecnicaData ||
      configurazioneScelta?.posizioni?.[0]?.tecnica ||
      configurazioneScelta?.posizioni?.[0]?.personalizzazione?.tecnica ||
      null,
    posizioni,
    confezionamento: configurazioneScelta?.confezionamento_richiesto,
    nomeLavorazione: configurazioneScelta?.nome_lavorazione,
    nomeLavorazioneManuale: configurazioneScelta?.nome_lavorazione_manuale,
    nomeLavorazioneNoIncrementale: configurazioneScelta?.nome_lavorazione_no_incrementale,
  }
}
